import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Button, Modal, TextInput } from "@opendate-app/setlist";
import client from "../../../shared/api/client";
import { toastError, toastSuccess } from "../../../shared/toastHelper";
import { clockLabel } from "./scheduleFormatters";

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

// What the server will save: one line per position, day and hours, then rows that share hours and headcount.
export const weekTemplateRows = (shifts, positions) => {
  const perDay = new Map();
  shifts
    // The server also skips 24 hour shifts, which a week line can't hold.
    .filter((shift) => !shift.calendar_event_id && shift.can_edit && shift.start_clock !== shift.end_clock)
    .forEach((shift) => {
      const day = new Date(`${shift.work_date}T00:00:00Z`).getUTCDay();
      const key = [shift.staff_position_id, day, shift.start_clock, shift.end_clock].join("|");
      perDay.set(key, (perDay.get(key) || 0) + 1);
    });
  const rows = new Map();
  perDay.forEach((count, key) => {
    const [positionId, day, start, end] = key.split("|");
    const rowKey = [positionId, start, end, count].join("|");
    rows.set(rowKey, [...(rows.get(rowKey) || []), Number(day)]);
  });
  return Array.from(rows, ([key, days]) => {
    const [positionId, start, end, count] = key.split("|");
    const position = positions.find((candidate) => String(candidate.id) === positionId);
    return {
      key,
      name: position?.name || "Position",
      color: position?.color_hex,
      headcount: Number(count),
      days: days.sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b)).map((day) => DAY_NAMES[day]).join(", "),
      hours: `${clockLabel(start)}–${clockLabel(end)}`,
    };
  });
};

const SaveWeekTemplateModal = ({ schedule, savePath, venueOwnershipId, defaultName, onClose, onSaved }) => {
  const [name, setName] = useState(defaultName);
  const [saving, setSaving] = useState(false);
  const rows = useMemo(() => weekTemplateRows(schedule.shifts, schedule.rows), [schedule]);
  const showShifts = schedule.shifts.filter((shift) => shift.calendar_event_id).length;

  const save = async () => {
    if (saving || !name.trim() || rows.length === 0) return;
    setSaving(true);
    try {
      const { data } = await client.post(savePath, {
        source_week: schedule.week.starts_on,
        shift_template: { kind: "week", name, location: String(venueOwnershipId) },
      });
      toastSuccess(`Saved “${name.trim()}”. Apply it to any week from Week templates.`);
      onSaved({ value: data.id, label: name.trim(), venue_ownership_id: venueOwnershipId });
    } catch (error) {
      setSaving(false);
      toastError(error?.response?.data?.errors?.[0] || "Could not save the template.");
    }
  };

  return (
    <Modal
      open
      width={600}
      showClose={false}
      onClose={onClose}
      title="Save this week as a template"
      description="Saves this week's shifts that aren't tied to a show as a week template. Show shifts come from event templates, so they're left out. Who's assigned isn't saved, only the position, days and hours."
      footer={
        <div className="d-flex justify-content-end" style={{ gap: 12, width: "100%" }}>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={save} disabled={saving || !name.trim() || rows.length === 0}>
            Save template
          </Button>
        </div>
      }
    >
      <div style={{ marginBottom: 16 }}>
        <TextInput id="week-template-name" label="Template name" maxLength={120} value={name} onChange={(event) => setName(event.target.value)} />
      </div>
      <div style={{ borderTop: "1px solid var(--sl-border-secondary)", margin: "0 -24px", maxHeight: "50vh", overflowY: "auto" }}>
        {rows.map((row) => (
          <div
            key={row.key}
            className="save-week-row"
            style={{ display: "flex", gap: 16, justifyContent: "space-between", alignItems: "center", padding: "12px 24px", borderBottom: "1px solid var(--sl-border-secondary)", fontSize: 14, lineHeight: "20px" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: row.color || "var(--sl-fg-quaternary)", flexShrink: 0 }} />
              <span style={{ fontWeight: 600, color: "var(--sl-text-primary)" }}>{row.name}</span>
              <span style={{ color: "var(--sl-text-tertiary)" }}>{`× ${row.headcount}`}</span>
            </div>
            <span style={{ color: "var(--sl-text-tertiary)", whiteSpace: "nowrap" }}>{`${row.days} · ${row.hours}`}</span>
          </div>
        ))}
        <div style={{ padding: "12px 24px", fontSize: 13, lineHeight: "18px", color: "var(--sl-text-quaternary)" }}>
          {rows.length === 0 ? "This week has no shifts outside of shows to save." : ""}
          {rows.length > 0 && showShifts > 0 ? `${showShifts} ${showShifts === 1 ? "shift" : "shifts"} tied to shows left out` : ""}
        </div>
      </div>
    </Modal>
  );
};

SaveWeekTemplateModal.propTypes = {
  schedule: PropTypes.shape({
    week: PropTypes.shape({ starts_on: PropTypes.string.isRequired }).isRequired,
    rows: PropTypes.array.isRequired,
    shifts: PropTypes.array.isRequired,
  }).isRequired,
  savePath: PropTypes.string.isRequired,
  venueOwnershipId: PropTypes.number.isRequired,
  defaultName: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default SaveWeekTemplateModal;
